// src/accounts/components/AccountRoleGrantModal.jsx
//
// Grant a user an account-scoped role, or edit one they already hold.
// Reuses admin/components/UserFormModal.jsx's exact shape (UserPicker →
// RolePicker → success panel) scoped down to one Account instead of the
// whole org. Replaces AccountDetailPage's old always-open inline form.
//
// size="lg": matches UserFormModal — RolePicker needs room for a role's
// name plus what it grants.

import { useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Modal, FormField, Button, Alert, SkeletonText, RolePicker, useDiscardConfirm } from "../../shared/ui";
import UserPicker from "../../components/UserPicker";

export default function AccountRoleGrantModal({
  open,
  binding,
  roles,
  rolesLoading,
  rolesError,
  onClose,
  onSubmit,
}) {
  const isEdit = Boolean(binding);

  const [userId, setUserId] = useState(binding?.userId ?? null);
  const [roleIds, setRoleIds] = useState(binding?.roleIds ?? []);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);
  const [dirty, setDirty] = useState(false);

  function handleClose() {
    setError(null);
    setSubmitting(false);
    setSaved(null);
    setDirty(false);
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !saved, handleClose);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await onSubmit({ userId, roleIds });
      setSaved(result);
    } catch (err) {
      setError(err?.message || "Save failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Only on edit: an empty selection revokes every role this user holds on
  // this Account. On create there's nothing to lose, so it's not worth
  // warning about — and the submit button is disabled until at least one
  // role is picked anyway.
  const willRemoveAllRoles = isEdit && roleIds.length === 0 && (binding.roleIds || []).length > 0;

  return (
    <>
      <Modal
        open={open}
        size="lg"
        title={isEdit ? `Edit roles — ${binding.username}` : "Grant an account role"}
        onClose={requestClose}
      >
        {saved ? (
          <div className="flex flex-col gap-4">
            <Alert
              variant="success"
              icon={CheckCircle2}
              title={isEdit ? `Roles updated for "${saved.username}"` : `Role granted to "${saved.username}"`}
            >
              Effective immediately, on every Project under this Account.
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            {error && (
              <Alert variant="danger" icon={XCircle} title="Couldn't save">
                {error}
              </Alert>
            )}

            <FormField label="User" required>
              {isEdit ? (
                <p className="text-sm text-ink bg-gray-50 border border-line rounded-card px-3 py-2">
                  {binding.username} ({binding.email})
                </p>
              ) : (
                <UserPicker
                  value={userId}
                  onChange={(v) => {
                    setUserId(v);
                    setDirty(true);
                  }}
                />
              )}
            </FormField>

            <FormField
              label="Roles"
              helperText="Saving replaces this user's account-scoped roles with exactly this selection."
            >
              {rolesLoading ? (
                <div className="py-3 px-3 border border-line rounded-card">
                  <SkeletonText lines={4} />
                </div>
              ) : rolesError ? (
                <Alert variant="danger" icon={XCircle} title="Couldn't load assignable roles">
                  {rolesError} Close this dialog and retry.
                </Alert>
              ) : (
                <RolePicker
                  roles={roles}
                  value={roleIds}
                  onChange={(next) => {
                    setRoleIds(next);
                    setDirty(true);
                  }}
                  emptyMessage='No account-level roles found in the catalog yet (e.g. "account_admin").'
                />
              )}
            </FormField>

            {willRemoveAllRoles && (
              <Alert variant="warning" icon={AlertTriangle} title="This will revoke every role">
                Saving with nothing selected removes {binding.username}&apos;s access to this
                Account entirely.
              </Alert>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                disabled={!userId || (!isEdit && roleIds.length === 0) || rolesLoading || Boolean(rolesError)}
              >
                {isEdit ? "Save changes" : "Grant role"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

AccountRoleGrantModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** null → grant mode; a binding → edit mode, seeded from it. */
  binding: PropTypes.shape({
    userId: PropTypes.number,
    username: PropTypes.string,
    email: PropTypes.string,
    roleIds: PropTypes.arrayOf(PropTypes.number),
  }),
  roles: PropTypes.array.isRequired,
  rolesLoading: PropTypes.bool,
  rolesError: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
